import { createApi } from '@reduxjs/toolkit/query/react'
import { SHARED_TAGS } from '../slices/constants'
import { generateRPCQuery, getBaseQuery } from './apiUtils'
import apiConstants from './constants'

export const usersApi = createApi({
  reducerPath: 'usersApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: [
    'UserDetails',
    'UserSegments',
    SHARED_TAGS.USERS,
    'UserAvailableSegments'
  ],
  endpoints: (builder) => ({
    getUserDetailsByID: builder.query({
      query: (requestParams: { UserID: string }) =>
        generateRPCQuery(
          apiConstants.USER_RPC_PATH,
          'retrieveUserById',
          requestParams
        ),
      providesTags: [{ type: 'UserDetails' }]
    }),
    getUserCampaigns: builder.query({
      query: (requestParams: { UserID: string }) =>
        generateRPCQuery(
          apiConstants.USER_CAMPAIGNS_RPC_PATH,
          'getUserCampaigns',
          requestParams
        )
    }),
    getUserOffers: builder.query({
      query: (requestParams: { UserID: string }) =>
        generateRPCQuery(
          apiConstants.USER_OFFERS_RPC_PATH,
          'getUserRedemptions',
          requestParams
        )
    }),
    getUserUpdateCards: builder.query({
      query: (requestParams: { UserID: string }) =>
        generateRPCQuery(
          apiConstants.USER_RPC_PATH,
          'getUserAvailableUpdateCards',
          requestParams
        )
    }),
    getUserProviders: builder.query({
      query: (requestParams: { UserID: string }) =>
        generateRPCQuery(
          apiConstants.USER_PROVIDERS_RPC_PATH,
          'getUserProviders',
          requestParams
        )
    }),
    insertUserToTargetGroup: builder.mutation({
      query: (requestParams: { UserID: string; TargetGroupID: number }) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'insertUserToTargetGroup',
          requestParams
        ),
      invalidatesTags: [
        { type: 'UserDetails' },
        { type: 'UserSegments' },
        { type: 'UserAvailableSegments' }
      ]
    }),
    deleteUserFromTargetGroup: builder.mutation({
      query: (requestParams: { UserID: string; TargetGroupID: number }) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'deleteUserFromTargetGroup',
          requestParams
        ),
      invalidatesTags: [
        { type: 'UserDetails' },
        { type: 'UserSegments' },
        { type: 'UserAvailableSegments' }
      ]
    }),
    suspendUser: builder.mutation({
      query: (requestParams: UserOptionsUpdateParams) =>
        generateRPCQuery(
          apiConstants.BASE_RPC_PATH,
          'suspendUser',
          requestParams
        ),
      invalidatesTags: [{ type: 'UserDetails' }]
    }),
    reactivateUser: builder.mutation({
      query: (requestParams: UserOptionsUpdateParams) =>
        generateRPCQuery(
          apiConstants.BASE_RPC_PATH,
          'reactivateUser',
          requestParams
        ),
      invalidatesTags: [{ type: 'UserDetails' }]
    }),
    optOutUser: builder.mutation({
      query: (requestParams: UserOptionsUpdateParams) =>
        generateRPCQuery(
          apiConstants.BASE_RPC_PATH,
          'optOutUser',
          requestParams
        ),
      invalidatesTags: [{ type: 'UserDetails' }]
    }),
    optInUser: builder.mutation({
      query: (requestParams: UserOptionsUpdateParams) =>
        generateRPCQuery(
          apiConstants.BASE_RPC_PATH,
          'optInUser',
          requestParams
        ),
      invalidatesTags: [{ type: 'UserDetails' }]
    }),
    getUserWalletTransactions: builder.query({
      query: (requestParams: GetUserTransactionsParam) =>
        generateRPCQuery(
          apiConstants.USER_TRANSACTIONS_RPC_PATH,
          'getWalletTransactionsForUser',
          requestParams
        )
    }),
    deleteUser: builder.mutation({
      query: (requestParams: UserOptionsUpdateParams) =>
        generateRPCQuery(
          apiConstants.USER_RPC_PATH,
          'softDeleteUser',
          requestParams
        ),
      invalidatesTags: [SHARED_TAGS.USERS]
    }),
    updateUserTestMode: builder.mutation({
      query: (requestParams: UserOptionsUpdateParams) =>
        generateRPCQuery(
          apiConstants.USER_RPC_PATH,
          'updateUserTestMode',
          requestParams
        ),
      invalidatesTags: [{ type: 'UserDetails' }]
    }),
    getUserSegments: builder.query({
      query: (requestParams: GetUserSegmentsParam) =>
        generateRPCQuery(
          apiConstants.USER_RPC_PATH,
          'getUserSegments',
          requestParams
        ),
      providesTags: [{ type: 'UserSegments' }]
    }),
    getAvailableSegmentsByUserID: builder.query({
      query: (requestParams: GetAvailableSegmentsParam) =>
        generateRPCQuery(
          apiConstants.USER_RPC_PATH,
          'getAvailableSegmentsByUserID',
          requestParams
        ),
      providesTags: [{ type: 'UserAvailableSegments' }]
    })
  })
})

export const {
  useDeleteUserFromTargetGroupMutation,
  useInsertUserToTargetGroupMutation,
  useOptInUserMutation,
  useOptOutUserMutation,
  useSuspendUserMutation,
  useReactivateUserMutation,
  useGetUserDetailsByIDQuery,
  useGetUserCampaignsQuery,
  useGetUserOffersQuery,
  useGetUserProvidersQuery,
  useGetUserUpdateCardsQuery,
  useGetUserWalletTransactionsQuery,
  useDeleteUserMutation,
  useUpdateUserTestModeMutation,
  useGetUserSegmentsQuery,
  useGetAvailableSegmentsByUserIDQuery
} = usersApi
